// src/components/tools/invoice-generator/InvoiceGeneratorTool.jsx
// Comprehensive Invoice & Quotation Generator for Small Businesses
import React, { useState, useEffect, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ArrowLeft, Plus, Search, Filter, MoreVertical, Edit2, Trash2, Eye,
  Download, Send, Copy, Check, X, ChevronRight, ChevronDown, Calendar,
  Receipt, FileText, Users, DollarSign, Clock, AlertCircle, CheckCircle,
  XCircle, Building2, Mail, Phone, MapPin, Globe, CreditCard, Percent,
  Hash, Printer, RefreshCw, TrendingUp, TrendingDown, ArrowUpRight,
  Loader2, Save, PlusCircle, MinusCircle, Image, Upload, Settings,
  FileUp, Calculator, Banknote, AlertTriangle, Star, Package
} from 'lucide-react';

// ============================================================
// CONSTANTS
// ============================================================

const DocumentType = {
  INVOICE: 'invoice',
  QUOTATION: 'quotation'
};

const DocumentStatus = {
  DRAFT: 'draft',
  SENT: 'sent',
  VIEWED: 'viewed',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  PAID: 'paid',
  PARTIALLY_PAID: 'partially_paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled'
};

const PaymentTerms = {
  DUE_ON_RECEIPT: { value: 0, label: 'Due on Receipt' },
  NET_7: { value: 7, label: 'Net 7 Days' },
  NET_14: { value: 14, label: 'Net 14 Days' },
  NET_30: { value: 30, label: 'Net 30 Days' },
  NET_60: { value: 60, label: 'Net 60 Days' },
  CUSTOM: { value: -1, label: 'Custom' }
};

const Currencies = [
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' }
];

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  INVOICES_LIST: 'INVOICES_LIST',
  QUOTATIONS_LIST: 'QUOTATIONS_LIST',
  CLIENTS_LIST: 'CLIENTS_LIST',
  DOCUMENT_FORM: 'DOCUMENT_FORM',
  DOCUMENT_VIEW: 'DOCUMENT_VIEW',
  CLIENT_FORM: 'CLIENT_FORM',
  SETTINGS: 'SETTINGS'
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const formatCurrency = (amount, currency = 'ZAR') => {
  const curr = Currencies.find(c => c.code === currency) || Currencies[0];
  return `${curr.symbol}${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const generateDocumentNumber = (prefix, count) => {
  const year = new Date().getFullYear();
  const num = String(count + 1).padStart(4, '0');
  return `${prefix}-${year}-${num}`;
};

const calculateDueDate = (issueDate, paymentTermDays) => {
  const date = new Date(issueDate);
  date.setDate(date.getDate() + paymentTermDays);
  return date;
};

const isOverdue = (dueDate, status) => {
  if (status === DocumentStatus.PAID || status === DocumentStatus.CANCELLED) return false;
  const due = new Date(dueDate);
  return due < new Date();
};

const getStatusConfig = (status) => {
  const configs = {
    [DocumentStatus.DRAFT]: { label: 'Draft', color: 'gray', icon: FileText },
    [DocumentStatus.SENT]: { label: 'Sent', color: 'blue', icon: Send },
    [DocumentStatus.VIEWED]: { label: 'Viewed', color: 'purple', icon: Eye },
    [DocumentStatus.ACCEPTED]: { label: 'Accepted', color: 'green', icon: CheckCircle },
    [DocumentStatus.DECLINED]: { label: 'Declined', color: 'red', icon: XCircle },
    [DocumentStatus.PAID]: { label: 'Paid', color: 'emerald', icon: CheckCircle },
    [DocumentStatus.PARTIALLY_PAID]: { label: 'Partial', color: 'amber', icon: Clock },
    [DocumentStatus.OVERDUE]: { label: 'Overdue', color: 'red', icon: AlertCircle },
    [DocumentStatus.CANCELLED]: { label: 'Cancelled', color: 'gray', icon: XCircle }
  };
  return configs[status] || configs[DocumentStatus.DRAFT];
};

const loadImageDataUrl = (imageUrl) => new Promise((resolve, reject) => {
  if (!imageUrl) {
    resolve(null);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';

  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      reject(error);
    }
  };

  img.onerror = () => reject(new Error('Failed to load logo'));
  img.src = imageUrl;
});

// ============================================================
// UI COMPONENTS
// ============================================================

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', trend, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-start gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-${color}-500 to-${color}-600 shadow-lg shadow-${color}-500/25`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
    {trend && (
      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
        trend > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
      }`}>
        {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {Math.abs(trend)}%
      </div>
    )}
  </button>
);

const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
      active 
        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25' 
        : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count > 0 && (
      <span className={`px-1.5 py-0.5 rounded-full text-xs ${
        active ? 'bg-white/20' : 'bg-gray-200'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// ============================================================
// LINE ITEMS EDITOR
// ============================================================

const LineItemsEditor = ({ items, onChange, currency, taxRate }) => {
  const addItem = () => {
    onChange([...items, {
      id: Date.now(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: true
    }]);
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount = subtotal * (item.discount / 100);
    return subtotal - discountAmount;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="hidden md:grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase px-2">
        <div className="col-span-5">Description</div>
        <div className="col-span-2 text-center">Qty</div>
        <div className="col-span-2 text-right">Unit Price</div>
        <div className="col-span-2 text-right">Total</div>
        <div className="col-span-1"></div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item.id} className="bg-gray-50 rounded-xl p-4">
            <div className="grid grid-cols-12 gap-3 items-start">
              {/* Description */}
              <div className="col-span-12 md:col-span-5">
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  placeholder="Item description..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              {/* Quantity */}
              <div className="col-span-4 md:col-span-2">
                <label className="md:hidden text-xs text-gray-500 mb-1 block">Qty</label>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              {/* Unit Price */}
              <div className="col-span-4 md:col-span-2">
                <label className="md:hidden text-xs text-gray-500 mb-1 block">Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unitPrice}
                  onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-right focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              {/* Total */}
              <div className="col-span-3 md:col-span-2 flex items-center justify-end">
                <span className="font-medium text-gray-900">
                  {formatCurrency(calculateItemTotal(item), currency)}
                </span>
              </div>
              
              {/* Remove */}
              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => removeItem(index)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Extra options row */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={item.tax}
                  onChange={(e) => updateItem(index, 'tax', e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600"
                />
                Apply Tax ({taxRate}%)
              </label>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Discount:</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={item.discount}
                  onChange={(e) => updateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                  className="w-16 px-2 py-1 border border-gray-200 rounded text-sm text-center"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Item Button */}
      <button
        onClick={addItem}
        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all flex items-center justify-center gap-2"
      >
        <PlusCircle className="w-5 h-5" />
        Add Line Item
      </button>
    </div>
  );
};

// ============================================================
// CLIENT SELECTOR
// ============================================================

const ClientSelector = ({ clients, selectedId, onSelect, onCreateNew }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredClients = useMemo(() => {
    if (!searchQuery) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(c => 
      c.name?.toLowerCase().includes(q) || 
      c.email?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q)
    );
  }, [clients, searchQuery]);

  const selectedClient = clients.find(c => c.id === selectedId);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">Client *</label>
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl hover:border-gray-300 transition-colors text-left"
      >
        {selectedClient ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-medium">
                {selectedClient.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-900">{selectedClient.name}</p>
              <p className="text-sm text-gray-500">{selectedClient.email}</p>
            </div>
          </div>
        ) : (
          <span className="text-gray-500">Select a client...</span>
        )}
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 max-h-80 overflow-hidden">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search clients..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto">
              {filteredClients.map(client => (
                <button
                  key={client.id}
                  onClick={() => { onSelect(client.id); setIsOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left ${
                    selectedId === client.id ? 'bg-indigo-50' : ''
                  }`}
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-600 font-medium">
                      {client.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{client.name}</p>
                    <p className="text-sm text-gray-500">{client.email}</p>
                  </div>
                  {selectedId === client.id && (
                    <Check className="w-5 h-5 text-indigo-600" />
                  )}
                </button>
              ))}
              
              {filteredClients.length === 0 && (
                <p className="text-center text-gray-500 py-4 text-sm">No clients found</p>
              )}
            </div>
            
            <div className="p-3 border-t border-gray-100">
              <button
                onClick={() => { onCreateNew(); setIsOpen(false); }}
                className="w-full py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add New Client
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================
// DOCUMENT FORM
// ============================================================

const DocumentForm = ({ 
  type, 
  document, 
  clients, 
  settings,
  onSave, 
  onCancel,
  onCreateClient,
  saving 
}) => {
  const isNew = !document?.id;
  const [formData, setFormData] = useState({
    type,
    clientId: '',
    documentNumber: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    paymentTerms: 30,
    reference: '',
    notes: '',
    terms: settings?.defaultTerms || '',
    items: [{ id: Date.now(), description: '', quantity: 1, unitPrice: 0, discount: 0, tax: true }],
    discount: 0,
    discountType: 'percentage', // 'percentage' or 'fixed'
    status: DocumentStatus.DRAFT,
    ...document
  });

  useEffect(() => {
    if (isNew) {
      // Generate document number
      const prefix = type === DocumentType.INVOICE ? (settings?.invoicePrefix || 'INV') : (settings?.quotePrefix || 'QUO');
      const num = generateDocumentNumber(prefix, settings?.documentCount || 0);
      setFormData(prev => ({ ...prev, documentNumber: num }));
    }
  }, [isNew, type, settings]);

  useEffect(() => {
    // Calculate due date based on payment terms
    if (formData.issueDate && formData.paymentTerms >= 0) {
      const dueDate = calculateDueDate(formData.issueDate, formData.paymentTerms);
      setFormData(prev => ({ ...prev, dueDate: dueDate.toISOString().split('T')[0] }));
    }
  }, [formData.issueDate, formData.paymentTerms]);

  // Calculate totals
  const calculations = useMemo(() => {
    const subtotal = formData.items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice * (1 - item.discount / 100);
      return sum + itemTotal;
    }, 0);

    const discountAmount = formData.discountType === 'percentage' 
      ? subtotal * (formData.discount / 100)
      : formData.discount;

    const taxableAmount = formData.items.reduce((sum, item) => {
      if (!item.tax) return sum;
      const itemTotal = item.quantity * item.unitPrice * (1 - item.discount / 100);
      return sum + itemTotal;
    }, 0);

    const taxRate = settings?.taxRate || 15;
    const taxAmount = (taxableAmount - (taxableAmount / subtotal * discountAmount || 0)) * (taxRate / 100);

    const total = subtotal - discountAmount + taxAmount;

    return { subtotal, discountAmount, taxableAmount, taxAmount, total, taxRate };
  }, [formData.items, formData.discount, formData.discountType, settings]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.clientId) {
      alert('Please select a client');
      return;
    }
    if (formData.items.length === 0 || !formData.items.some(i => i.description)) {
      alert('Please add at least one line item');
      return;
    }
    onSave({
      ...formData,
      ...calculations,
      currency: settings?.currency || 'ZAR'
    });
  };

  const selectedClient = clients.find(c => c.id === formData.clientId);

  return (
    <form onSubmit={handleSubmit} className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isNew ? 'Create' : 'Edit'} {type === DocumentType.INVOICE ? 'Invoice' : 'Quotation'}
              </h1>
              <p className="text-gray-500">{formData.documentNumber}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isNew ? 'Create' : 'Update'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Bill To</h2>
              <ClientSelector
                clients={clients}
                selectedId={formData.clientId}
                onSelect={(id) => setFormData(prev => ({ ...prev, clientId: id }))}
                onCreateNew={onCreateClient}
              />
              
              {selectedClient && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <p className="font-medium text-gray-900">{selectedClient.company || selectedClient.name}</p>
                  {selectedClient.company && <p className="text-sm text-gray-600">{selectedClient.name}</p>}
                  <p className="text-sm text-gray-500 mt-1">{selectedClient.email}</p>
                  {selectedClient.address && (
                    <p className="text-sm text-gray-500 mt-1 whitespace-pre-line">{selectedClient.address}</p>
                  )}
                </div>
              )}
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Line Items</h2>
              <LineItemsEditor
                items={formData.items}
                onChange={(items) => setFormData(prev => ({ ...prev, items }))}
                currency={settings?.currency || 'ZAR'}
                taxRate={settings?.taxRate || 15}
              />
            </div>

            {/* Notes & Terms */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={4}
                    placeholder="Notes visible to client..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Terms & Conditions</label>
                  <textarea
                    value={formData.terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                    rows={4}
                    placeholder="Payment terms, conditions..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Document Details */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <h2 className="font-semibold text-gray-900">Details</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Number</label>
                <input
                  type="text"
                  value={formData.documentNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, documentNumber: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: parseInt(e.target.value) }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                >
                  {Object.values(PaymentTerms).map(term => (
                    <option key={term.value} value={term.value}>{term.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference / PO Number</label>
                <input
                  type="text"
                  value={formData.reference}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Optional reference..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                />
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100 p-6 space-y-3">
              <h2 className="font-semibold text-gray-900">Summary</h2>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{formatCurrency(calculations.subtotal, settings?.currency)}</span>
              </div>

              {/* Discount Input */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Discount</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={formData.discount}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                    className="w-20 px-2 py-1 border border-gray-200 rounded text-sm text-right"
                  />
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                    className="px-2 py-1 border border-gray-200 rounded text-sm"
                  >
                    <option value="percentage">%</option>
                    <option value="fixed">{Currencies.find(c => c.code === settings?.currency)?.symbol || 'R'}</option>
                  </select>
                </div>
              </div>

              {calculations.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>Discount Amount</span>
                  <span>-{formatCurrency(calculations.discountAmount, settings?.currency)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax ({calculations.taxRate}%)</span>
                <span className="font-medium">{formatCurrency(calculations.taxAmount, settings?.currency)}</span>
              </div>

              <div className="pt-3 border-t border-indigo-200">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-indigo-600">
                    {formatCurrency(calculations.total, settings?.currency)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// DOCUMENT VIEW
// ============================================================

const DocumentView = ({ document, client, settings, onEdit, onBack, onMarkPaid, onSend, onDuplicate, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);

  const statusConfig = getStatusConfig(document.status);
  const isInvoice = document.type === DocumentType.INVOICE;
  const overdue = isOverdue(document.dueDate, document.status);

  const handlePrint = () => {
    setShowMenu(false);
    window.print();
  };

  const handleDownloadPdf = async () => {
    try {
      setShowMenu(false);

      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const rightX = pageWidth - 40;
      let cursorY = 48;

      let logoDataUrl = null;
      if (settings?.logoUrl) {
        try {
          logoDataUrl = await loadImageDataUrl(settings.logoUrl);
        } catch (error) {
          console.warn('Could not load logo for PDF export:', error);
        }
      }

      if (logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', 40, 36, 90, 45, undefined, 'FAST');
      }

      const companyStartX = logoDataUrl ? 142 : 40;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text(settings?.companyName || 'Your Company', companyStartX, cursorY);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const companyLines = pdf.splitTextToSize(
        [settings?.companyAddress, settings?.companyEmail, settings?.companyPhone].filter(Boolean).join('\n'),
        250
      );
      pdf.text(companyLines, companyStartX, cursorY + 16);

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(24);
      pdf.text(isInvoice ? 'INVOICE' : 'QUOTATION', rightX, 55, { align: 'right' });
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text(document.documentNumber || '', rightX, 74, { align: 'right' });

      cursorY = 120;
      pdf.setDrawColor(226, 232, 240);
      pdf.line(40, cursorY, pageWidth - 40, cursorY);
      cursorY += 24;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('BILL TO', 40, cursorY);
      pdf.setFont('helvetica', 'normal');
      const clientBlock = [
        client?.company || client?.name || '',
        client?.company ? client?.name : '',
        client?.email || '',
        client?.address || ''
      ].filter(Boolean).join('\n');
      pdf.text(pdf.splitTextToSize(clientBlock, 230), 40, cursorY + 14);

      pdf.setFont('helvetica', 'bold');
      pdf.text('DETAILS', rightX - 180, cursorY);
      pdf.setFont('helvetica', 'normal');
      const detailLines = [
        `Issue Date: ${formatDate(document.issueDate)}`,
        `Due Date: ${formatDate(document.dueDate)}`,
        document.reference ? `Reference: ${document.reference}` : null
      ].filter(Boolean);
      pdf.text(detailLines, rightX - 180, cursorY + 14);

      cursorY += 86;

      const tableBody = (document.items || []).map((item) => {
        const qty = Number(item.quantity || 0);
        const unit = Number(item.unitPrice || 0);
        const lineTotal = qty * unit * (1 - (Number(item.discount || 0) / 100));
        return [
          item.description || '',
          String(qty),
          formatCurrency(unit, document.currency),
          formatCurrency(lineTotal, document.currency)
        ];
      });

      autoTable(pdf, {
        startY: cursorY,
        head: [['Description', 'Qty', 'Unit Price', 'Total']],
        body: tableBody.length ? tableBody : [['No line items', '', '', '']],
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 6 },
        columnStyles: {
          0: { cellWidth: 260 },
          1: { halign: 'center', cellWidth: 50 },
          2: { halign: 'right', cellWidth: 100 },
          3: { halign: 'right', cellWidth: 100 }
        },
        margin: { left: 40, right: 40 }
      });

      const totalsY = (pdf.lastAutoTable?.finalY || cursorY) + 18;
      const totalsX = pageWidth - 220;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(`Subtotal: ${formatCurrency(document.subtotal, document.currency)}`, totalsX, totalsY);
      if (Number(document.discountAmount || 0) > 0) {
        pdf.text(`Discount: -${formatCurrency(document.discountAmount, document.currency)}`, totalsX, totalsY + 14);
      }
      pdf.text(`Tax (${document.taxRate || 0}%): ${formatCurrency(document.taxAmount, document.currency)}`, totalsX, totalsY + 28);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text(`Total Due: ${formatCurrency(document.total, document.currency)}`, totalsX, totalsY + 48);

      let footerY = totalsY + 78;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);

      if (document.notes) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Notes', 40, footerY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(pdf.splitTextToSize(document.notes, pageWidth - 80), 40, footerY + 12);
        footerY += 44;
      }

      if (document.terms) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Terms & Conditions', 40, footerY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(pdf.splitTextToSize(document.terms, pageWidth - 80), 40, footerY + 12);
      }

      if (isInvoice && settings?.bankDetails) {
        const blockY = Math.min(pageHeight - 70, footerY + 58);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Payment Details', 40, blockY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(pdf.splitTextToSize(settings.bankDetails, pageWidth - 80), 40, blockY + 12);
      }

      const safeName = (document.documentNumber || 'document').replace(/[^\w-]+/g, '_');
      pdf.save(`${safeName}.pdf`);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-white rounded-xl shadow-sm border border-gray-200">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{document.documentNumber}</h1>
              <div className="flex items-center gap-3 mt-1">
                <StatusBadge status={overdue && document.status !== DocumentStatus.PAID ? DocumentStatus.OVERDUE : document.status} />
                {overdue && document.status !== DocumentStatus.PAID && (
                  <span className="text-sm text-red-600">
                    Overdue by {Math.ceil((new Date() - new Date(document.dueDate)) / (1000 * 60 * 60 * 24))} days
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isInvoice && document.status !== DocumentStatus.PAID && (
              <button
                onClick={onMarkPaid}
                className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Mark as Paid
              </button>
            )}
            
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>

            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-white rounded-xl border border-gray-200"
              >
                <MoreVertical className="w-5 h-5 text-gray-600" />
              </button>

              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 py-1">
                    <button onClick={() => { onSend(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Send className="w-4 h-4" /> Send to Client
                    </button>
                    <button onClick={() => { onDuplicate(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Copy className="w-4 h-4" /> Duplicate
                    </button>
                    <button onClick={handleDownloadPdf} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Download className="w-4 h-4" /> Download PDF
                    </button>
                    <button onClick={handlePrint} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Printer className="w-4 h-4" /> Print
                    </button>
                    <hr className="my-1" />
                    <button onClick={() => { onDelete(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Document Preview */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Company Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white">
            <div className="flex justify-between items-start">
              <div>
                {settings?.logoUrl ? (
                  <img src={settings.logoUrl} alt="Logo" className="h-12 mb-4" />
                ) : (
                  <h2 className="text-2xl font-bold mb-2">{settings?.companyName || 'Your Company'}</h2>
                )}
                <p className="text-white/80 whitespace-pre-line">{settings?.companyAddress}</p>
              </div>
              <div className="text-right">
                <h1 className="text-3xl font-bold">{isInvoice ? 'INVOICE' : 'QUOTATION'}</h1>
                <p className="text-white/80 mt-2">{document.documentNumber}</p>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="p-8">
            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Bill To */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Bill To</h3>
                <p className="font-semibold text-gray-900">{client?.company || client?.name}</p>
                {client?.company && <p className="text-gray-600">{client?.name}</p>}
                <p className="text-gray-600">{client?.email}</p>
                <p className="text-gray-600 whitespace-pre-line mt-1">{client?.address}</p>
              </div>

              {/* Document Info */}
              <div className="text-right">
                <div className="space-y-1">
                  <p className="text-sm"><span className="text-gray-500">Issue Date:</span> <span className="font-medium">{formatDate(document.issueDate)}</span></p>
                  <p className="text-sm"><span className="text-gray-500">Due Date:</span> <span className="font-medium">{formatDate(document.dueDate)}</span></p>
                  {document.reference && (
                    <p className="text-sm"><span className="text-gray-500">Reference:</span> <span className="font-medium">{document.reference}</span></p>
                  )}
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <table className="w-full mb-8">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 text-sm font-semibold text-gray-900">Description</th>
                  <th className="text-center py-3 text-sm font-semibold text-gray-900">Qty</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-900">Unit Price</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-900">Total</th>
                </tr>
              </thead>
              <tbody>
                {document.items?.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-4 text-gray-900">{item.description}</td>
                    <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                    <td className="py-4 text-right text-gray-600">{formatCurrency(item.unitPrice, document.currency)}</td>
                    <td className="py-4 text-right font-medium text-gray-900">
                      {formatCurrency(item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100), document.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(document.subtotal, document.currency)}</span>
                </div>
                {document.discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(document.discountAmount, document.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax ({document.taxRate}%)</span>
                  <span className="font-medium">{formatCurrency(document.taxAmount, document.currency)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t-2 border-gray-200">
                  <span className="font-bold text-gray-900">Total Due</span>
                  <span className="text-xl font-bold text-indigo-600">{formatCurrency(document.total, document.currency)}</span>
                </div>
              </div>
            </div>

            {/* Notes & Terms */}
            {(document.notes || document.terms) && (
              <div className="mt-8 pt-8 border-t border-gray-200 grid grid-cols-2 gap-8">
                {document.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Notes</h3>
                    <p className="text-gray-600 text-sm whitespace-pre-line">{document.notes}</p>
                  </div>
                )}
                {document.terms && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Terms & Conditions</h3>
                    <p className="text-gray-600 text-sm whitespace-pre-line">{document.terms}</p>
                  </div>
                )}
              </div>
            )}

            {/* Payment Info */}
            {isInvoice && settings?.bankDetails && (
              <div className="mt-8 p-4 bg-gray-50 rounded-xl">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Payment Details</h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">{settings.bankDetails}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// CLIENT FORM
// ============================================================

const ClientForm = ({ client, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    vatNumber: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'South Africa',
    notes: '',
    ...client
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      alert('Name and email are required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {client?.id ? 'Edit Client' : 'Add Client'}
        </h1>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
          <input
            type="text"
            value={formData.vatNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
          <textarea
            value={formData.address}
            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
            <input
              type="text"
              value={formData.province}
              onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
            <input
              type="text"
              value={formData.postalCode}
              onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            placeholder="Internal notes about this client..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {client?.id ? 'Update' : 'Create'} Client
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// SETTINGS FORM
// ============================================================

const SettingsForm = ({ settings, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    companyWebsite: '',
    vatNumber: '',
    logoUrl: '',
    currency: 'ZAR',
    taxRate: 15,
    taxLabel: 'VAT',
    invoicePrefix: 'INV',
    quotePrefix: 'QUO',
    defaultTerms: 'Payment is due within the specified payment terms. Late payments may incur additional charges.',
    bankDetails: '',
    ...settings
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoice Settings</h1>
          <p className="text-gray-500">Configure your business details and preferences</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Company Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            Business Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VAT/Tax Number</label>
              <input
                type="text"
                value={formData.vatNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.companyEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, companyEmail: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.companyPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, companyPhone: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea
              value={formData.companyAddress}
              onChange={(e) => setFormData(prev => ({ ...prev, companyAddress: e.target.value }))}
              rows={2}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              type="url"
              value={formData.companyWebsite}
              onChange={(e) => setFormData(prev => ({ ...prev, companyWebsite: e.target.value }))}
              placeholder="https://"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        {/* Tax & Currency */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-indigo-600" />
            Tax & Currency
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              >
                {Currencies.map(c => (
                  <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.taxRate}
                onChange={(e) => setFormData(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Label</label>
              <input
                type="text"
                value={formData.taxLabel}
                onChange={(e) => setFormData(prev => ({ ...prev, taxLabel: e.target.value }))}
                placeholder="VAT, GST, Tax..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Document Numbering */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Hash className="w-5 h-5 text-indigo-600" />
            Document Numbering
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix</label>
              <input
                type="text"
                value={formData.invoicePrefix}
                onChange={(e) => setFormData(prev => ({ ...prev, invoicePrefix: e.target.value.toUpperCase() }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">Preview: {formData.invoicePrefix}-2024-0001</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote Prefix</label>
              <input
                type="text"
                value={formData.quotePrefix}
                onChange={(e) => setFormData(prev => ({ ...prev, quotePrefix: e.target.value.toUpperCase() }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">Preview: {formData.quotePrefix}-2024-0001</p>
            </div>
          </div>
        </div>

        {/* Banking */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Banknote className="w-5 h-5 text-indigo-600" />
            Payment Details
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Details</label>
            <textarea
              value={formData.bankDetails}
              onChange={(e) => setFormData(prev => ({ ...prev, bankDetails: e.target.value }))}
              rows={4}
              placeholder="Bank: First National Bank&#10;Account Name: Your Company&#10;Account Number: 1234567890&#10;Branch Code: 250655"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl font-mono text-sm"
            />
          </div>
        </div>

        {/* Default Terms */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Default Terms
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
            <textarea
              value={formData.defaultTerms}
              onChange={(e) => setFormData(prev => ({ ...prev, defaultTerms: e.target.value }))}
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Settings
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// DOCUMENTS LIST
// ============================================================

const DocumentsList = ({ 
  type, 
  documents, 
  clients, 
  onView, 
  onEdit, 
  onCreate, 
  onDelete,
  onConvertToInvoice 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const filteredDocs = useMemo(() => {
    let filtered = documents.filter(d => d.type === type);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        d.documentNumber?.toLowerCase().includes(q) ||
        clients.find(c => c.id === d.clientId)?.name?.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.issueDate) - new Date(a.issueDate);
      }
      if (sortBy === 'amount') {
        return (b.total || 0) - (a.total || 0);
      }
      return 0;
    });

    return filtered;
  }, [documents, type, searchQuery, statusFilter, sortBy, clients]);

  const isInvoice = type === DocumentType.INVOICE;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-900">
          {isInvoice ? 'Invoices' : 'Quotations'}
        </h2>
        <button
          onClick={onCreate}
          className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New {isInvoice ? 'Invoice' : 'Quotation'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-xl"
        >
          <option value="all">All Status</option>
          <option value={DocumentStatus.DRAFT}>Draft</option>
          <option value={DocumentStatus.SENT}>Sent</option>
          {isInvoice && <option value={DocumentStatus.PAID}>Paid</option>}
          {isInvoice && <option value={DocumentStatus.OVERDUE}>Overdue</option>}
          {!isInvoice && <option value={DocumentStatus.ACCEPTED}>Accepted</option>}
          {!isInvoice && <option value={DocumentStatus.DECLINED}>Declined</option>}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-xl"
        >
          <option value="date">Sort by Date</option>
          <option value="amount">Sort by Amount</option>
        </select>
      </div>

      {/* List */}
      {filteredDocs.length === 0 ? (
        <EmptyState
          icon={isInvoice ? Receipt : FileText}
          title={`No ${isInvoice ? 'invoices' : 'quotations'} yet`}
          description={`Create your first ${isInvoice ? 'invoice' : 'quotation'} to get started`}
          action={
            <button
              onClick={onCreate}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create {isInvoice ? 'Invoice' : 'Quotation'}
            </button>
          }
        />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Number</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Client</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Date</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Status</th>
                  <th className="text-right py-4 px-6 text-sm font-semibold text-gray-900">Amount</th>
                  <th className="text-right py-4 px-6 text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map(doc => {
                  const client = clients.find(c => c.id === doc.clientId);
                  const overdue = isOverdue(doc.dueDate, doc.status);

                  return (
                    <tr key={doc.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <button
                          onClick={() => onView(doc)}
                          className="font-medium text-indigo-600 hover:text-indigo-700"
                        >
                          {doc.documentNumber}
                        </button>
                      </td>
                      <td className="py-4 px-6">
                        <p className="font-medium text-gray-900">{client?.company || client?.name}</p>
                        {client?.company && <p className="text-sm text-gray-500">{client?.name}</p>}
                      </td>
                      <td className="py-4 px-6 text-gray-600">{formatDate(doc.issueDate)}</td>
                      <td className="py-4 px-6">
                        <StatusBadge status={overdue && isInvoice && doc.status !== DocumentStatus.PAID ? DocumentStatus.OVERDUE : doc.status} />
                      </td>
                      <td className="py-4 px-6 text-right font-medium text-gray-900">
                        {formatCurrency(doc.total, doc.currency)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => onView(doc)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onEdit(doc)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {!isInvoice && doc.status === DocumentStatus.ACCEPTED && (
                            <button
                              onClick={() => onConvertToInvoice(doc)}
                              className="p-2 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg"
                              title="Convert to Invoice"
                            >
                              <ArrowUpRight className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (window.confirm('Delete this document?')) onDelete(doc.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const InvoiceGeneratorTool = ({ bookId, customStyling = {}, toolsService, onBack }) => {
  const [viewState, setViewState] = useState(ViewState.DASHBOARD);
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [documentType, setDocumentType] = useState(DocumentType.INVOICE);

  // Load data
  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesData, quotesData, clientsData, settingsData] = await Promise.all([
        toolsService.getToolData(bookId, 'invoices'),
        toolsService.getToolData(bookId, 'quotations'),
        toolsService.getToolData(bookId, 'clients'),
        toolsService.getToolSettings(bookId, 'invoice-generator')
      ]);
      
      setDocuments([...invoicesData, ...quotesData]);
      setClients(clientsData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const invoices = documents.filter(d => d.type === DocumentType.INVOICE);
    const quotes = documents.filter(d => d.type === DocumentType.QUOTATION);
    
    const totalOutstanding = invoices
      .filter(d => d.status !== DocumentStatus.PAID && d.status !== DocumentStatus.CANCELLED)
      .reduce((sum, d) => sum + (d.total || 0), 0);
    
    const totalPaid = invoices
      .filter(d => d.status === DocumentStatus.PAID)
      .reduce((sum, d) => sum + (d.total || 0), 0);
    
    const overdueCount = invoices.filter(d => 
      isOverdue(d.dueDate, d.status) && d.status !== DocumentStatus.PAID
    ).length;

    return {
      totalInvoices: invoices.length,
      totalQuotes: quotes.length,
      totalClients: clients.length,
      totalOutstanding,
      totalPaid,
      overdueCount
    };
  }, [documents, clients]);

  // Handlers
  const handleSaveDocument = async (data) => {
    setSaving(true);
    try {
      const slug = data.type === DocumentType.INVOICE ? 'invoices' : 'quotations';
      await toolsService.saveToolData(bookId, slug, data);
      
      // Update document count in settings
      await toolsService.saveToolSettings(bookId, 'invoice-generator', {
        ...settings,
        documentCount: (settings.documentCount || 0) + (data.id ? 0 : 1)
      });
      
      await loadData();
      setViewState(ViewState.DASHBOARD);
      setSelectedDocument(null);
    } catch (error) {
      console.error('Error saving document:', error);
      alert('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClient = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'clients', data);
      await loadData();
      setViewState(ViewState.DOCUMENT_FORM);
      setSelectedClient(null);
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolSettings(bookId, 'invoice-generator', data);
      await loadData();
      setViewState(ViewState.DASHBOARD);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (id) => {
    try {
      const doc = documents.find(d => d.id === id);
      const slug = doc?.type === DocumentType.INVOICE ? 'invoices' : 'quotations';
      await toolsService.deleteToolData(bookId, slug, id);
      await loadData();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await toolsService.deleteToolData(bookId, 'clients', id);
      await loadData();
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleMarkPaid = async (doc) => {
    try {
      await toolsService.saveToolData(bookId, 'invoices', {
        ...doc,
        status: DocumentStatus.PAID,
        paidAt: new Date().toISOString()
      });
      await loadData();
    } catch (error) {
      console.error('Error marking as paid:', error);
    }
  };

  const handleConvertToInvoice = async (quote) => {
    const invoiceData = {
      ...quote,
      id: undefined,
      type: DocumentType.INVOICE,
      documentNumber: generateDocumentNumber(settings.invoicePrefix || 'INV', settings.documentCount || 0),
      status: DocumentStatus.DRAFT,
      sourceQuote: quote.id,
      issueDate: new Date().toISOString().split('T')[0]
    };
    setSelectedDocument(invoiceData);
    setDocumentType(DocumentType.INVOICE);
    setViewState(ViewState.DOCUMENT_FORM);
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Render based on view state
  switch (viewState) {
    case ViewState.DOCUMENT_FORM:
      return (
        <DocumentForm
          type={documentType}
          document={selectedDocument}
          clients={clients}
          settings={settings}
          onSave={handleSaveDocument}
          onCancel={() => { setViewState(ViewState.DASHBOARD); setSelectedDocument(null); }}
          onCreateClient={() => { setSelectedClient(null); setViewState(ViewState.CLIENT_FORM); }}
          saving={saving}
        />
      );

    case ViewState.DOCUMENT_VIEW:
      return (
        <DocumentView
          document={selectedDocument}
          client={clients.find(c => c.id === selectedDocument?.clientId)}
          settings={settings}
          onEdit={() => { setDocumentType(selectedDocument.type); setViewState(ViewState.DOCUMENT_FORM); }}
          onBack={() => { setViewState(ViewState.DASHBOARD); setSelectedDocument(null); }}
          onMarkPaid={() => handleMarkPaid(selectedDocument)}
          onSend={() => alert('Send functionality coming soon')}
          onDuplicate={() => {
            setSelectedDocument({ ...selectedDocument, id: undefined, status: DocumentStatus.DRAFT });
            setDocumentType(selectedDocument.type);
            setViewState(ViewState.DOCUMENT_FORM);
          }}
          onDelete={() => {
            if (window.confirm('Delete this document?')) {
              handleDeleteDocument(selectedDocument.id);
              setViewState(ViewState.DASHBOARD);
            }
          }}
        />
      );

    case ViewState.CLIENT_FORM:
      return (
        <ClientForm
          client={selectedClient}
          onSave={handleSaveClient}
          onCancel={() => { setViewState(ViewState.DASHBOARD); setSelectedClient(null); }}
          saving={saving}
        />
      );

    case ViewState.SETTINGS:
      return (
        <SettingsForm
          settings={settings}
          onSave={handleSaveSettings}
          onCancel={() => setViewState(ViewState.DASHBOARD)}
          saving={saving}
        />
      );

    case ViewState.INVOICES_LIST:
      return (
        <div className="p-6">
          <button onClick={() => setViewState(ViewState.DASHBOARD)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <DocumentsList
            type={DocumentType.INVOICE}
            documents={documents}
            clients={clients}
            onView={(doc) => { setSelectedDocument(doc); setViewState(ViewState.DOCUMENT_VIEW); }}
            onEdit={(doc) => { setSelectedDocument(doc); setDocumentType(DocumentType.INVOICE); setViewState(ViewState.DOCUMENT_FORM); }}
            onCreate={() => { setSelectedDocument(null); setDocumentType(DocumentType.INVOICE); setViewState(ViewState.DOCUMENT_FORM); }}
            onDelete={handleDeleteDocument}
          />
        </div>
      );

    case ViewState.QUOTATIONS_LIST:
      return (
        <div className="p-6">
          <button onClick={() => setViewState(ViewState.DASHBOARD)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <DocumentsList
            type={DocumentType.QUOTATION}
            documents={documents}
            clients={clients}
            onView={(doc) => { setSelectedDocument(doc); setViewState(ViewState.DOCUMENT_VIEW); }}
            onEdit={(doc) => { setSelectedDocument(doc); setDocumentType(DocumentType.QUOTATION); setViewState(ViewState.DOCUMENT_FORM); }}
            onCreate={() => { setSelectedDocument(null); setDocumentType(DocumentType.QUOTATION); setViewState(ViewState.DOCUMENT_FORM); }}
            onDelete={handleDeleteDocument}
            onConvertToInvoice={handleConvertToInvoice}
          />
        </div>
      );

    case ViewState.CLIENTS_LIST:
      return (
        <div className="p-6">
          <button onClick={() => setViewState(ViewState.DASHBOARD)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Clients</h2>
            <button
              onClick={() => { setSelectedClient(null); setViewState(ViewState.CLIENT_FORM); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Client
            </button>
          </div>

          {clients.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No clients yet"
              description="Add your first client to start creating invoices"
              action={
                <button
                  onClick={() => { setSelectedClient(null); setViewState(ViewState.CLIENT_FORM); }}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" /> Add Client
                </button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map(client => (
                <div key={client.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-indigo-600 font-semibold text-lg">
                        {client.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setSelectedClient(client); setViewState(ViewState.CLIENT_FORM); }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this client?')) handleDeleteClient(client.id);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-gray-900">{client.company || client.name}</h3>
                  {client.company && <p className="text-sm text-gray-600">{client.name}</p>}
                  <p className="text-sm text-gray-500 mt-1">{client.email}</p>
                  {client.phone && <p className="text-sm text-gray-500">{client.phone}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      );

    default: // DASHBOARD
      return (
        <div className="h-full overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Invoice Generator</h1>
                  <p className="text-gray-500">Create and manage invoices & quotations</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewState(ViewState.SETTINGS)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <button
                  onClick={() => { setSelectedDocument(null); setDocumentType(DocumentType.QUOTATION); setViewState(ViewState.DOCUMENT_FORM); }}
                  className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" /> New Quote
                </button>
                <button
                  onClick={() => { setSelectedDocument(null); setDocumentType(DocumentType.INVOICE); setViewState(ViewState.DOCUMENT_FORM); }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Receipt className="w-4 h-4" /> New Invoice
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={DollarSign}
                label="Outstanding"
                value={formatCurrency(stats.totalOutstanding, settings.currency)}
                color="amber"
                onClick={() => setViewState(ViewState.INVOICES_LIST)}
              />
              <StatCard
                icon={CheckCircle}
                label="Paid"
                value={formatCurrency(stats.totalPaid, settings.currency)}
                color="emerald"
              />
              <StatCard
                icon={Receipt}
                label="Invoices"
                value={stats.totalInvoices}
                subValue={stats.overdueCount > 0 ? `${stats.overdueCount} overdue` : undefined}
                color="indigo"
                onClick={() => setViewState(ViewState.INVOICES_LIST)}
              />
              <StatCard
                icon={Users}
                label="Clients"
                value={stats.totalClients}
                color="purple"
                onClick={() => setViewState(ViewState.CLIENTS_LIST)}
              />
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setViewState(ViewState.INVOICES_LIST)}
                className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all text-left"
              >
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Invoices</h3>
                  <p className="text-sm text-gray-500">{stats.totalInvoices} total</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </button>

              <button
                onClick={() => setViewState(ViewState.QUOTATIONS_LIST)}
                className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-lg transition-all text-left"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Quotations</h3>
                  <p className="text-sm text-gray-500">{stats.totalQuotes} total</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </button>

              <button
                onClick={() => setViewState(ViewState.CLIENTS_LIST)}
                className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all text-left"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Clients</h3>
                  <p className="text-sm text-gray-500">{stats.totalClients} contacts</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </button>
            </div>

            {/* Recent Documents */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Recent Documents</h2>
              </div>
              
              {documents.length === 0 ? (
                <div className="p-8 text-center">
                  <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No documents yet</p>
                  <p className="text-sm text-gray-400">Create your first invoice or quotation</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {documents.slice(0, 5).map(doc => {
                    const client = clients.find(c => c.id === doc.clientId);
                    const isInvoice = doc.type === DocumentType.INVOICE;
                    
                    return (
                      <button
                        key={doc.id}
                        onClick={() => { setSelectedDocument(doc); setViewState(ViewState.DOCUMENT_VIEW); }}
                        className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 text-left"
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          isInvoice ? 'bg-indigo-100' : 'bg-purple-100'
                        }`}>
                          {isInvoice ? (
                            <Receipt className="w-5 h-5 text-indigo-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-purple-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{doc.documentNumber}</p>
                          <p className="text-sm text-gray-500">{client?.name || 'Unknown client'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{formatCurrency(doc.total, doc.currency)}</p>
                          <StatusBadge status={doc.status} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      );
  }
};

export default InvoiceGeneratorTool;
